/**
 * cargo_route.ts — sky-freighter kinematics (PURE — no three, no DOM;
 * node-testable, the air_choreo doctrine: every pose is a function of the
 * convoy row + wall-clock t, so every client sees the same convoy in the
 * same place with zero networking).
 *
 * A convoy flies a straight great-line from its exporter to its importer at
 * cruise altitude, climbing out of the origin and descending into the
 * destination (smoothstep ends). Three escorts hold a delta formation —
 * port, starboard, trail-high — each with a gentle patrol weave.
 */

export interface CargoRouteRow {
  fromX: number; fromZ: number;
  toX: number; toZ: number;
  /** Unix seconds. */
  departS: number;
  arriveS: number;
}

export interface CargoPose {
  x: number; z: number;
  /** Altitude ABOVE GROUND intent (the module seats it on sampled terrain). */
  alt: number;
  /** Unit heading in XZ. */
  hx: number; hz: number;
  /** 0..1 route progress (clamped). */
  k: number;
}

export const CARGO_CRUISE_ALT = 300;   // m AGL on the leg
export const CARGO_END_ALT = 60;       // m AGL over the endpoints
export const ESCORT_COUNT = 3;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (v: number): number => { const c = clamp01(v); return c * c * (3 - 2 * c); };

/** Where the freighter is at wall-clock `nowS`. */
export function cargoPose(r: CargoRouteRow, nowS: number): CargoPose {
  const dur = Math.max(1, r.arriveS - r.departS);
  const k = clamp01((nowS - r.departS) / dur);
  const x = r.fromX + (r.toX - r.fromX) * k;
  const z = r.fromZ + (r.toZ - r.fromZ) * k;
  const dx = r.toX - r.fromX, dz = r.toZ - r.fromZ;
  const len = Math.hypot(dx, dz) || 1;
  // Climb out over ~12% of the leg, descend over the last ~12%.
  const lift = Math.min(smooth(k / 0.12), smooth((1 - k) / 0.12));
  return {
    x, z,
    alt: CARGO_END_ALT + (CARGO_CRUISE_ALT - CARGO_END_ALT) * lift,
    hx: dx / len, hz: dz / len, k,
  };
}

/** Escort slot i (0..2) around the freighter: port, starboard, trail-high —
 *  with a slow patrol weave so the wing reads alive, not welded on. */
export function escortPose(r: CargoRouteRow, i: number, nowS: number): CargoPose {
  const c = cargoPose(r, nowS);
  // Perpendicular in XZ.
  const px = -c.hz, pz = c.hx;
  const w = Math.sin(nowS * 0.5 + i * 2.4) * 6;   // the weave
  const bob = Math.sin(nowS * 0.33 + i * 1.7) * 4;
  let ox = 0, oz = 0, oa = 0;
  if (i === 0) { ox = px * (55 + w) - c.hx * 20; oz = pz * (55 + w) - c.hz * 20; oa = 10; }
  else if (i === 1) { ox = -px * (55 + w) - c.hx * 20; oz = -pz * (55 + w) - c.hz * 20; oa = 10; }
  else { ox = -c.hx * 85 + px * w; oz = -c.hz * 85 + pz * w; oa = 34; }
  return { x: c.x + ox, z: c.z + oz, alt: c.alt + oa + bob, hx: c.hx, hz: c.hz, k: c.k };
}

/** The ballistic crash: from the kill point the hull carries forward and
 *  falls; returns where it meets the ground plane (the caller grounds Y).
 *  Deterministic from the kill position + heading so every client agrees. */
export function crashPoint(x: number, z: number, alt: number, hx: number, hz: number): { x: number; z: number } {
  // Forward throw ~2.2 m per m of altitude (a shallow dying glide).
  const throwM = Math.min(900, alt * 2.2);
  return { x: x + hx * throwM, z: z + hz * throwM };
}
